import React from 'react'

import { Standfirst1, Body3, Heading4, Join, Space } from '@gousto-internal/citrus-react'
import classnames from 'classnames'
import Immutable from 'immutable'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'

import { useFetchRecipe } from 'routes/Menu/context/recipeContext'
import { useIsAddAProteinPaintedDoorEnabled } from 'routes/Menu/hooks/useIsAddAProteinPaintedDoorEnabled'
import { getNumPortions } from 'selectors/basket'

import { useAllCollections } from '../../domains/collections'
import { getMenuCategoryIdForDetails } from '../../selectors/menuRecipeDetails'
import { Title } from '../Recipe'
import { RecipeAlternativeOptions } from '../RecipeAlternativeOptions'
import { RecipeTag } from '../RecipeTag'
import { AttributeGrid } from './AttributeGrid'
import { DetailCarousel } from './Carousel'
import { DetailAddRecipe } from './DetailAddRecipe'
import { DetailAllergenIngredientsContainer } from './DetailAllergenIngredients'
import { DetailError } from './DetailError'
import { DetailImage as Image } from './DetailImage'
import { DetailIngredients } from './DetailIngredients'
import { DetailPerPortion } from './DetailPerPortion'
import { RecipeRating } from './Rating'
import { RecipeCustomisationContainer } from './RecipeCustomisation/RecipeCustomisationContainer'
import { RecipeDisclaimer } from './RecipeDisclaimer'

import css from './Detail.css'
import titleCss from './DetailTitle.css'

export const Detail = (props) => {
  const {
    id,
    chosenSideRecipeId,
    onClose,
    media,
    title,
    view,
    count,
    average,
    description,
    youWillNeed,
    equipment,
    position,
    surcharge,
    isFineDineIn,
    fetchRecipe,
  } = props
  const allCollections = useAllCollections()

  let currentCollectionId = useSelector(getMenuCategoryIdForDetails)
  if (!currentCollectionId) {
    currentCollectionId = Object.values(allCollections.toJSON())
      .filter((c) => !!c)
      .find((collectionObj) => collectionObj.recipesInCollection?.indexOf(id) > -1).id
  }
  const numPortions = useSelector(getNumPortions)
  const { error, loading, recipe, reload } = useFetchRecipe(fetchRecipe)

  const recipeLegalDetailId = chosenSideRecipeId || id
  const titleClass = classnames(titleCss.containerLG, {
    [titleCss.detailHeading]: view === 'detail',
    [titleCss.fineDineInDetailHeading]: view === 'fineDineInDetail',
  })

  const dietType =
    recipe && recipe.getIn(['attributes', 'diet_type'])
      ? recipe.getIn(['attributes', 'diet_type']).toJS()
      : { name: '', slug: '' }
  const shouldShowAddAProteinPaintedDoor = useIsAddAProteinPaintedDoorEnabled(
    id,
    currentCollectionId,
    dietType,
  )

  return (
    <div
      className={css.modalContainer}
      role="button"
      tabIndex={0}
      onKeyPress={onClose}
      onClick={onClose}
      data-testing="menuRecipeDetailsClose"
    >
      <div
        role="button"
        tabIndex={0}
        className={css.container}
        onKeyPress={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={css.header}>
          <div className={css.stickyHeader}>
            <div className={css.titleFlex}>
              <Title className={titleClass} />
              <span
                className={css.closeIcon}
                role="button"
                tabIndex={0}
                onKeyPress={onClose}
                onClick={onClose}
                data-testing="menuRecipeDetailsClose"
              />
            </div>
          </div>
          <div className={css.badges}>
            <div className={css.titleHidden} data-testing="recipeDetailTitle">
              <Title className={titleClass} />
            </div>
            <span className={css.recipeTagHolder}>
              <RecipeTag />
            </span>
            <RecipeRating count={count} average={average} />
          </div>
        </div>
        <div className={css.imageContainer}>
          {isFineDineIn ? (
            <DetailCarousel images={media} />
          ) : (
            <Image media={media} title={title} view={view} />
          )}
        </div>
        <div className={css.sectionPanel}>
          <div className={css.variantsContainer}>
            <RecipeAlternativeOptions
              originalId={id}
              recipeId={id}
              isOnDetailScreen
              categoryId={currentCollectionId}
            />
          </div>
          {shouldShowAddAProteinPaintedDoor && (
            <RecipeCustomisationContainer recipeId={recipe.toJS().id} dietType={dietType} />
          )}
          <Join with={<Space size={4} />}>
            <Heading4>Recipe details</Heading4>
            <Standfirst1 data-testing="recipeDetailsDescription">{description}</Standfirst1>
            <AttributeGrid />
            <RecipeDisclaimer recipeId={id} />
            {equipment && !!equipment.size && (
              <Body3>Equipment required: {equipment.toJS().join(', ')} </Body3>
            )}
            {youWillNeed && !!youWillNeed.size && (
              <Body3>
                What you&#8217;ll need:{' '}
                {youWillNeed.map((item, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <span key={idx}>
                    {item}
                    {youWillNeed.size - 1 !== idx && ', '}
                  </span>
                ))}
              </Body3>
            )}
          </Join>
        </div>
        {error ? (
          <DetailError reload={reload} />
        ) : (
          <>
            <div className={css.row} data-testing="recipeDetailsIngredients">
              <DetailIngredients loading={loading} recipe={recipe} />
            </div>
            <div className={css.row}>
              <DetailPerPortion
                recipeId={recipeLegalDetailId}
                loading={loading}
                recipe={recipe}
                numPortions={numPortions}
              />
            </div>
            <div className={css.row} data-testing="recipeDetailsAllergenIngredients">
              <DetailAllergenIngredientsContainer
                recipeId={recipeLegalDetailId}
                recipe={recipe}
                loading={loading}
              />
            </div>
          </>
        )}
        <div className={css.stickyContainer}>
          <DetailAddRecipe id={id} view={view} surcharge={surcharge} position={position} />
        </div>
      </div>
    </div>
  )
}

Detail.propTypes = {
  id: PropTypes.string.isRequired,
  chosenSideRecipeId: PropTypes.string,
  media: PropTypes.instanceOf(Immutable.List).isRequired,
  title: PropTypes.string.isRequired,
  view: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  average: PropTypes.number,
  description: PropTypes.string.isRequired,
  youWillNeed: PropTypes.instanceOf(Immutable.List).isRequired,
  equipment: PropTypes.instanceOf(Immutable.List).isRequired,
  onClose: PropTypes.func.isRequired,
  position: PropTypes.number,
  isFineDineIn: PropTypes.bool,
  surcharge: PropTypes.number,
  fetchRecipe: PropTypes.func.isRequired,
}

Detail.defaultProps = {
  surcharge: 0,
  position: 0,
  average: 0,
  isFineDineIn: false,
  chosenSideRecipeId: null,
}
